import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useFetch:
 * A reusable hook that runs an async request function and manages:
 * - data: the latest successful response
 * - error: the latest error (excluding aborts)
 * - isLoading: whether the latest request is in flight
 * @param {function} requestFn - an async function that performs the request.
 * It receives an object with:
 *  - signal: AbortSignal to support cancellation
 * - args: array of arguments passed to run(...)
 * @param {array} deps - dependency array for memoizing requestFn
 * @param {object} options - configuration options
 *  - enabled: boolean to auto-run on mount/deps change (default: true)
 * - cancelPrevious: boolean to abort previous in-flight requests (default: true)
 * @return {object}
 *  - data: the latest successful response
 * - error: the latest error (excluding aborts)
 * - isLoading: whether the latest request is in flight
 * - run: function to manually trigger the request
 * - refetch: alias for run
 * - setData: function to manually set the data state
 *
 * It also supports:
 * - enabled: whether to auto-run on mount / dependency changes
 * - cancelPrevious: whether to abort a previous in-flight request when a new one starts
 * - stale-response protection: older requests can't overwrite newer ones
 */
export function useFetch(requestFn, deps = [], options = {}) {
  // Options with defaults:
  // enabled: if true, auto-run once on mount and whenever deps change
  // cancelPrevious: if true, abort any existing request before starting a new one
  const {
    enabled = true,         // auto-run on mount/deps change
    cancelPrevious = true,  // abort previous run() calls
  } = options;

  // Local state for consumers of the hook
  const [data, setData] = useState(null);       // holds the successful response
  const [error, setError] = useState(null);     // holds an error from requestFn (except AbortError)
  const [isLoading, setIsLoading] = useState(true); // true while request is running

  // Holds the current AbortController so we can cancel an in-flight request
  const abortRef = useRef(null);

  // Monotonically increasing request id used to prevent stale responses
  // (older requests finishing later should NOT overwrite newer results)
  const reqIdRef = useRef(0);

  /**
   * run(...args):
   * Manually triggers the request. You can pass arguments through to requestFn.
   * It returns the request result so callers can `await run(...)`.
   *
   * useCallback memoizes run so it doesn't get recreated every render
   * unless something in `deps` changes.
   */
  const run = useCallback(async (...args) => {
    // If configured to cancel previous requests, abort the currently running one (if any)
    if (cancelPrevious && abortRef.current) abortRef.current.abort();

    // Create a new abort controller for THIS request
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    // Increment request id and capture it for this invocation
    // If another request starts later, reqIdRef.current changes
    const reqId = ++reqIdRef.current;

    // Mark as loading and clear any previous error
    setIsLoading(true);
    setError(null);

    try {
      // Call the provided request function.
      // We pass both:
      // - signal: so axios/fetch can be aborted
      // - args: any arguments the caller passed to run(...)
      const res = await requestFn({ signal: ctrl.signal, args });

      // Only update data if this request is still the latest one
      // (prevents stale/older requests from overwriting new data)
      if (reqId === reqIdRef.current) setData(res);

      // Return value lets the caller do: `const res = await run(payload)`
      return res;
    } catch (err) {
      // Ignore abort errors (they're expected when cancelPrevious is true or unmount happens)
      if (err?.name !== "AbortError") {
        // Only store the error if this request is still the latest one
        if (reqId === reqIdRef.current) setError(err);

        // Re-throw so caller can handle it with try/catch if they want
        throw err;
      }
    } finally {
      // Only stop loading if this request is still the latest one
      // (prevents older request from turning loading off during a newer request)
      if (reqId === reqIdRef.current) setIsLoading(false);
    }
  }, deps);

  /**
   * Auto-run effect:
   * - If enabled is true, call run() on mount and whenever `run` changes (deps change)
   * - Cleanup aborts any in-flight request when the component unmounts
   */
  useEffect(() => {
    if (!enabled) return;          // do nothing if auto-run is disabled
    run();                         // start request
    return () => abortRef.current?.abort(); // cleanup: abort if still in-flight
  }, [enabled, run]);

  // Expose state + helpers:
  // - run/refetch: trigger the request
  // - setData: allow optimistic updates or manual state edits
  return { data, error, isLoading, run, refetch: run, setData };
}
